import { useEffect, useState } from 'react';
import { useLocation, useHistory, Redirect } from 'react-router-dom';
import styled from '@emotion/styled';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { setStep, setSchoolName } from '../santeMobiliteSlice';
import SanteMobiliteLayout from '../components/layouts/SanteMobiliteLayout';
import ContractDatesStep from '../steps/ContractDatesStep';
import DestinationStep from '../steps/DestinationStep';
import SocialSecurityStep from '../steps/SocialSecurityStep';
import PersonalInfoStep from '../steps/PersonalInfoStep';
import FinalizationStep from '../steps/FinalizationStep';
import { isAuthenticated } from '@/services/utils';
import { authSuccess } from '@/redux/actions/authActions';
import { getMe } from '@/api/authApi';
import LocalStorageService from '@/services/LocalStorageService';

const Spacing = styled.div`
  height: 50px;
  @media (max-width: 600px) {
    height: 10px;
  }
`;

const SanteMobilitePage = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const history = useHistory();

  const step = useAppSelector(state => state.santeMobilite.step);
  const lastVerifiedStep = useAppSelector(state => state.santeMobilite.lastVerifiedStep);
  const [isLogged, setIsLogged] = useState<boolean>(!!isAuthenticated());
  const [authChecked, setAuthChecked] = useState(false);
  const isTempRegistered = useAppSelector(state => state.user.isTempRegistered);

  useEffect(() => {
    let cancelled = false;
    const verifyAuth = async () => {
      const localUser = isAuthenticated();
      if (!localUser) {
        if (!cancelled) {
          setIsLogged(false);
          setAuthChecked(true);
        }
        return;
      }
      try {
        const response = await getMe();
        if (!cancelled) {
          // Le résultat est remis DANS le store, pas seulement utilisé comme
          // preuve de session. Sans ce dispatch, `state.auth.data.user` reste
          // vide sur tout le wizard après un chargement de page, et tout ce qui
          // dépend des rôles est faux : `selectInclusionPath` ne voit aucun
          // rôle et renvoie 'paid', si bien qu'un étudiant INCLUSION se voit
          // facturer un parcours qui doit lui être gratuit.
          //
          // Le défaut ne se voyait pas en navigant dans l'application — la
          // connexion avait alors rempli le store — mais ce parcours se
          // recharge lui-même (reload après le fast register, redirection au
          // paiement). Le prix basculait donc de « Gratuit » à 150 € sous les
          // yeux de l'utilisateur, sur un simple F5.
          if (response?.data) {
            dispatch(authSuccess({ data: { user: response.data }, token: null }));
          }
          setIsLogged(true);
          setAuthChecked(true);
        }
      } catch {
        LocalStorageService.clearUser();
        LocalStorageService.clearToken();
        if (!cancelled) {
          setIsLogged(false);
          setAuthChecked(true);
        }
      }
    };
    verifyAuth();
    return () => {
      cancelled = true;
    };
  }, []);

  // Sync URL → Redux on initial load / navigation
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    // Capture ?school=... à l'arrivée pour rattacher l'inscription temporaire
    // à la bonne école si l'utilisateur n'est pas encore connecté.
    const schoolParam = params.get('school');
    if (schoolParam) {
      dispatch(setSchoolName(schoolParam.trim().toLowerCase()));
    }

    const stepParam = parseInt(params.get('step') || '1', 10);
    const desiredStep = isNaN(stepParam) ? 1 : stepParam;

    if (desiredStep > lastVerifiedStep + 1) {
      params.set('step', `${lastVerifiedStep + 1}`);
      history.replace({ search: params.toString() });
      return;
    }

    if (desiredStep < 1) {
      params.set('step', '1');
      history.replace({ search: params.toString() });
      return;
    }

    if (step !== desiredStep && desiredStep !== 0) {
      dispatch(setStep(desiredStep));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Sync Redux → URL when step changes from within components
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlStep = parseInt(params.get('step') || '1', 10);

    if (urlStep !== step) {
      params.set('step', step.toString());
      history.replace({ search: params.toString() });
    }
  }, [step, location.search, history]);

  const isUserConnected = isLogged || isTempRegistered;

  const renderStep = () => {
    switch (step) {
      case 1:
        return <ContractDatesStep />;
      case 2:
        return <DestinationStep />;
      case 3:
        return <SocialSecurityStep />;
      case 4:
        if (!authChecked) return null;
        // Si déjà connecté, on saute le step PersonalInfo et on va direct à la finalisation.
        return isUserConnected ? <FinalizationStep /> : <PersonalInfoStep />;
      case 5:
        if (!authChecked) return null;
        return isUserConnected ? (
          <FinalizationStep />
        ) : (
          <Redirect to="/insurance/health-mobility?step=1" />
        );
      default:
        return <Redirect to="/insurance/health-mobility?step=1" />;
    }
  };

  return (
    <SanteMobiliteLayout>
      <Spacing />
      {renderStep()}
      {/* Espace en bas pour ne pas que le contenu soit masqué par la sticky.
          On laisse ~80px en mobile car la sticky fait ~70px. */}
      <Spacing />
    </SanteMobiliteLayout>
  );
};

export default SanteMobilitePage;
